// file: /src/PhotoGallery.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight, FaInfoCircle, FaTimes } from 'react-icons/fa';
import './css/photoGallery.css';
import NewReservation from '../Form';                         // adjust if needed
import useRestaurantData from '../../Hooks/useRestaurantData.js';
import InitPage from '../Loading/InitPage.js';                // splash / loader

// Memo-ise all slot positions so we don't recreate objects every render
const useSlotStyles = () => useMemo(() => ({
  left:            { top: '200px', left: '5%',  width: '25%', transform: 'translateX(0%)',    opacity: 1, zIndex: 1000 },
  center:          { top: '200px', left: '50%', width: '30%', transform: 'translateX(-50%)', opacity: 1, zIndex: 1001 },
  right:           { top: '200px', left: '95%', width: '25%', transform: 'translateX(-100%)',opacity: 1, zIndex: 1000 },
  offScreenLeft:   { top: '200px', left: '5%',  width: '25%', transform: 'translateX(-150%)',opacity: 0, pointerEvents: 'none', zIndex: 999 },
  offScreenRight:  { top: '200px', left: '95%', width: '25%', transform: 'translateX(50%)',  opacity: 0, pointerEvents: 'none', zIndex: 999 },
  hidden:          { opacity: 0, pointerEvents: 'none', zIndex: 998, width: 0, height: 0, overflow: 'hidden' },
  fullScreen:      {
    top: '50%', left: '50%', width: '100vw', height: '100vh',
    borderRadius: '0', zIndex: 1020, cursor: 'default',
    position: 'fixed', overflow: 'hidden',
    transform: 'translate(-50%, -50%)'
  },
}), []);

export default function PhotoGallery({
  restaurantName,
}) {
  const restaurantData         = useRestaurantData();
  const [initAnimationDone, setInitAnimationDone] = useState(false);
  const [initVisible,      setInitVisible]        = useState(true);
  const [enlargedMenuId,   setEnlargedMenuId]     = useState(null);
  const [finalSelectionId, setFinalSelectionId]   = useState(null);
  const [infoModalId,      setInfoModalId]        = useState(null);
  const [isMobile,         setIsMobile]           = useState(window.innerWidth <= 1000);
  const [currentIndex,     setCurrentIndex]       = useState(0);

  const slotStyles = useSlotStyles();

  // ---- Tafelboeken.be promo (same logic as InitPage) ----
  const restaurantDisplayName =
    (window.restaurantName && window.restaurantName.trim() !== "")
      ? window.restaurantName
      : (restaurantName && restaurantName.trim() !== "")
        ? restaurantName
        : "dit restaurant";

  const tafelboekenUrlBase = "https://www.tafelboeken.be";
  const tafelboekenUrl = `${tafelboekenUrlBase}/?utm_source=app&utm_medium=gallery&utm_campaign=glimlach${
    restaurantDisplayName !== "dit restaurant"
      ? `&ref=${encodeURIComponent(restaurantDisplayName)}`
      : ""
  }`;
  // -------------------------------------------------------

  const fallbackMenuData = [
    {
      id: 1,
      title: restaurantName
        ? `Reserveer bij ${restaurantName}`
        : 'Reserveer Uw Tafel',
      shortDescription:
        'Geniet van een heerlijke maaltijd bij ons restaurant.',
      longDescription:
        'Geniet van een heerlijke maaltijd bij ons restaurant. Bekijk meer restaurants op tafelboeken.be',
      image:
        'https://static.reservaties.net/backgrounds/standard.webp',
      primary: true,
    },
  ];

  const menuData = useMemo(() => {
    if (!restaurantData) return [];
    return restaurantData.menuData?.length
      ? restaurantData.menuData
      : fallbackMenuData;
  }, [restaurantData, fallbackMenuData]);

  function isMenuItemActive(menu) {
    if (menu.alwaysShow) return true;
    const now = new Date();

    if (menu.startDate) {
      const [y, m, d] = menu.startDate.split('-').map(Number);
      if (now < new Date(y, m - 1, d, 0, 0, 0)) return false;
    }
    if (menu.endDate) {
      const [y, m, d] = menu.endDate.split('-').map(Number);
      if (now > new Date(y, m - 1, d, 23, 59, 59)) return false;
    }

    if (menu.startTime && menu.endTime) {
      const [sh, sm] = menu.startTime.split(':').map(Number);
      const [eh, em] = menu.endTime.split(':').map(Number);
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh, sm);
      const todayEnd   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh, em);
      if (now < todayStart || now > todayEnd) return false;
    }

    return true;
  }

  const dataToRender = useMemo(() => {
    const activeItems = menuData.filter(isMenuItemActive);

    console.log(menuData);

    if (activeItems.length === 0) {
      console.log("No active menu items found, using fallback data instead");
      return fallbackMenuData;
    }
    // Only one active: always centered
    if (activeItems.length === 1) {
      return activeItems;
    }
    // Exactly two active: show side by side, ignore primary flags
    if (activeItems.length === 2) {
      return activeItems;
    }

    // More than two active: reorder based on primary flags
    const primaries = activeItems.filter(m => m.primary);
    const others    = activeItems.filter(m => !m.primary);

    if (primaries.length === 1) {
      // Single primary: put it in the center (index 1)
      return [others[0], primaries[0], ...others.slice(1)];
    }
    if (primaries.length === 2) {
      // Two primaries: put both first
      return [primaries[0], primaries[1], ...others];
    }
    if (primaries.length >= 3) {
      // Three or more primaries: first three positions are primaries
      return [...primaries.slice(0, 3), ...others];
    }
    // No primaries: default ordering
    return activeItems;
  }, [menuData, fallbackMenuData]);

  const totalItems = dataToRender.length;
  const showCarousel = !isMobile && totalItems > 3;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1000);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const canStartHidingInit =
    initAnimationDone && !!restaurantData;

  const handleImageClick = (id) => {
    if (!enlargedMenuId && !finalSelectionId) {
      const selectedItem = dataToRender.find((m) => m.id === id);
      window.currentReservationTicketItem = selectedItem;
      setEnlargedMenuId(id);
    }
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    setEnlargedMenuId(null);
    window.currentReservationTicketItem = null;
  };

  const handleNext = () =>
    setCurrentIndex((i) => Math.min(i + 1, totalItems - 3));

  const handlePrev = () =>
    setCurrentIndex((i) => Math.max(i - 1, 0));

  const getMenuItemStyle = (menu, idx) => {
    const isEnlarged = enlargedMenuId === menu.id;
    const hiddenAfterSelection =
      finalSelectionId !== null && finalSelectionId !== menu.id;

    if (isEnlarged) return slotStyles.fullScreen;
    if (hiddenAfterSelection)
      return { ...slotStyles.hidden, position: isMobile ? 'relative' : 'absolute' };

    if (isMobile) {
      return {
        position: 'relative',
        opacity: 1,
        pointerEvents: 'auto',
        cursor: finalSelectionId === null ? 'pointer' : 'default',
      };
    }

    if (!showCarousel && totalItems === 2) {
      const base = { width: '30%', opacity: 1, zIndex: 1000 };
      const leftTwo  = { ...base, top: '200px', left: '33%', transform: 'translateX(-50%)' };
      const rightTwo = { ...base, top: '200px', left: '67%', transform: 'translateX(-50%)' };
      return {
        ...(idx === 0 ? leftTwo : rightTwo),
        position: 'absolute',
        cursor: finalSelectionId === null ? 'pointer' : 'default',
      };
    }

    let style = slotStyles.hidden;
    if (showCarousel) {
      const rel = idx - currentIndex;
      style = rel < 0
        ? slotStyles.offScreenLeft
        : rel === 0
          ? slotStyles.left
          : rel === 1
            ? slotStyles.center
            : rel === 2
              ? slotStyles.right
              : slotStyles.offScreenRight;
    } else {
      if (totalItems === 1 && idx === 0)      style = slotStyles.center;
      else if (idx === 0)                     style = slotStyles.left;
      else if (idx === 1)                     style = slotStyles.center;
      else if (idx === 2)                     style = slotStyles.right;
    }
    return {
      ...style,
      position: 'absolute',
      cursor: finalSelectionId === null ? 'pointer' : 'default',
    };
  };

  const showArrows = showCarousel && !enlargedMenuId && !finalSelectionId;
  const infoItem   = infoModalId !== null
    ? dataToRender.find((m) => m.id === infoModalId)
    : null;

  return (
    <>
      {initVisible && (
        <InitPage
          language={navigator.language || 'nl'}
          onInitComplete={() => setInitAnimationDone(true)}
          shouldHide={canStartHidingInit}
          onHideComplete={() => setInitVisible(false)}
        />
      )}

      <div className="photo-gallery-page">
        <div className={`animated-menu-container ${enlargedMenuId ? 'item-enlarged' : ''}`}>
          {!enlargedMenuId && (
            <header className="experiences-header">
              <h1 className="page-title">Reserveer uw Tafel</h1>
              <p className="page-subtitle">
                Wilt u van een heerlijke maaltijd genieten? Selecteer reserveer
              </p>
            </header>
          )}

          {dataToRender.map((menu, idx) => {
            const style    = getMenuItemStyle(menu, idx);
            const showCard = style.opacity !== 0 && !finalSelectionId;

            return (
              <div
                key={menu.id}
                className={`menu-image-wrapper ${showCard ? 'has-description-card' : ''}`}
                style={style}
                onClick={() => style.pointerEvents !== 'none' && handleImageClick(menu.id)}
              >
                <button
                  className="info-button"
                  onClick={(e) => { e.stopPropagation(); setInfoModalId(menu.id); }}
                  aria-label="Meer info"
                >
                  <FaInfoCircle />
                </button>

                <div className="image-container">
                  <img src={menu.image} alt={menu.title} className="menu-bg-image" />
                </div>

                {showCard && (
                  <>
                    <div className="description-card">
                      <h3 className="description-card-title">{menu.title}</h3>
                      <p className="description-card-text">{menu.shortDescription}</p>
                    </div>
                    <button
                      className="book-button-for-image"
                      onClick={(e) => { e.stopPropagation(); handleImageClick(menu.id); }}
                    >
                      Reserveer
                    </button>
                  </>
                )}

                {enlargedMenuId === menu.id && (
                  <div className="enlarged-content-overlay">
                    <NewReservation
                      restaurantData={restaurantData || null}
                      initialMenuTitle={''}
                      selectedMenuId={menu.id}
                      onReservationSuccess={() => setEnlargedMenuId(null)}
                      onCloseRequest={handleCloseClick}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {showArrows && (
            <>
              <button
                className="carousel-arrow prev"
                onClick={handlePrev}
                disabled={currentIndex === 0}
              >
                <FaChevronLeft />
              </button>
              <button
                className="carousel-arrow next"
                onClick={handleNext}
                disabled={currentIndex >= totalItems - 3}
              >
                <FaChevronRight />
              </button>
            </>
          )}

          {enlargedMenuId && (
            <button className="close-button" onClick={handleCloseClick}>
              &times;
            </button>
          )}
        </div>

        {/* Bottom text link */}
        {!enlargedMenuId && (
          <div className="tb-promo-container tb-promo-footer">
            <a
              href={tafelboekenUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tb-promo-link montserrat-font"
              aria-label="Bezoek tafelboeken.be voor reviews, foto’s en meer."
            >
              <span className="tb-emoji" aria-hidden="true">☺️</span>
              <span>
                Bekijk foto's op <strong style={{ textDecoration: 'underline' }}>tafelboeken.be</strong>
              </span>
            </a>
          </div>
        )}

        

        {infoItem && (
          <div
            className="info-modal-overlay"
            onClick={() => setInfoModalId(null)}
          >
            <div
              className="info-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="info-modal-close"
                onClick={() => setInfoModalId(null)}
              >
                <FaTimes />
              </button>
              <img src={infoItem.image} alt={infoItem.title} className="info-modal-image" />
              <div className="info-modal-content">
                <h2 className="info-modal-title">{infoItem.title}</h2>
                <p className="info-modal-description">{infoItem.longDescription}</p>
                <button
                  className="modal-cta-button"
                  onClick={() => {
                    setInfoModalId(null);
                    setEnlargedMenuId(infoItem.id);
                  }}
                >
                  {infoItem.buttonText || 'Reserveer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
